import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Shield, User, Pencil, KeyRound, Copy, Info } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "sales_rep" };

function randomTempPassword() {
  // Human-readable temp password: 3 short lowercase words + 3-digit number
  const words = ["comet", "delta", "orbit", "swift", "riven", "north", "atlas", "nova", "clove", "amber", "onyx", "reef"];
  const pick = () => words[Math.floor(Math.random() * words.length)];
  const n = Math.floor(100 + Math.random() * 900);
  return `${pick()}-${pick()}-${n}`;
}

export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);

  // Add-member dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY);
  const [addSaving, setAddSaving] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // { name, email, password }

  // Edit-member dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "sales_rep" });
  const [editSaving, setEditSaving] = useState(false);

  // Reset-password dialog
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwJustSet, setPwJustSet] = useState(null); // {email, password}

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setAddForm({ ...EMPTY, password: randomTempPassword() });
    setJustCreated(null);
    setAddOpen(true);
  };

  const submitAdd = async () => {
    setAddSaving(true);
    try {
      await api.post("/users", addForm);
      setJustCreated({ name: addForm.name, email: addForm.email, password: addForm.password });
      toast.success("Team member added");
      setAddForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddSaving(false); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", role: u.role || "sales_rep" });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setEditSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, editForm);
      toast.success("Team member updated");
      setEditOpen(false);
      setEditUser(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEditSaving(false); }
  };

  const openReset = (u) => {
    setPwUser(u);
    setPwValue(randomTempPassword());
    setPwJustSet(null);
    setPwOpen(true);
  };

  const submitReset = async () => {
    if (pwValue.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwSaving(true);
    try {
      await api.post(`/users/${pwUser.id}/password`, { password: pwValue });
      setPwJustSet({ email: pwUser.email, password: pwValue });
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPwSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this team member?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Member removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text).then(() => toast.success(label));
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="text-3xl tracking-tight font-medium">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">Invite members, edit details and manage roles.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-member-btn" onClick={openAdd} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4 mr-1.5" /> Add member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{justCreated ? "Share these credentials" : "Add team member"}</DialogTitle>
              <DialogDescription className="sr-only">
                {justCreated ? "One-time credentials for the new team member." : "Create a new team member account."}
              </DialogDescription>
            </DialogHeader>
            {justCreated ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/60 border border-border rounded p-3">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    <strong>{justCreated.name}</strong> can now sign in with the credentials below.
                    We don&rsquo;t send an invite email yet — share this <em>out-of-band</em> (Slack DM, 1Password, secure note).
                    You can reset the password anytime from the Team page.
                  </span>
                </div>
                <CredRow label="Email" value={justCreated.email} onCopy={() => copy(justCreated.email, "Email copied")} />
                <CredRow label="Temp password" value={justCreated.password} mono onCopy={() => copy(justCreated.password, "Password copied")} />
                <DialogFooter>
                  <Button variant="outline" onClick={openAdd} data-testid="add-another">Add another</Button>
                  <Button onClick={() => setAddOpen(false)} data-testid="done-adding" className="bg-primary text-primary-foreground hover:bg-primary/90">Done</Button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input data-testid="member-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input data-testid="member-email" type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Temporary password</Label>
                      <div className="flex gap-1.5">
                        <Input data-testid="member-password" minLength={6} value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} />
                        <Button
                          type="button" variant="outline" size="icon"
                          onClick={() => setAddForm({ ...addForm, password: randomTempPassword() })}
                          title="Generate a new password"
                          data-testid="regen-password"
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Role</Label>
                      <select
                        data-testid="member-role"
                        className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                        value={addForm.role}
                        onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                      >
                        <option value="sales_rep">Sales Rep</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 border border-border rounded p-2.5">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>You pick a temporary password. Share it privately with the member — there&rsquo;s no invite email yet. You can reset it anytime from this page.</span>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="member-cancel">Cancel</Button>
                  <Button
                    onClick={submitAdd}
                    disabled={addSaving || !addForm.name || !addForm.email || addForm.password.length < 6}
                    data-testid="member-save"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {addSaving ? "Saving…" : "Add member"}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-secondary/60 border-b border-border text-left">
            <tr>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Role</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Joined</th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-accent/40 transition-colors duration-150">
                <td className="px-4 py-3 font-medium flex items-center gap-2">
                  {u.role === "admin"
                    ? <Shield className="h-4 w-4 text-primary" strokeWidth={1.75} />
                    : <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
                  {u.name}
                  {me?.id === u.id && <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-1">You</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground uppercase tracking-wider">
                    {u.role === "admin" ? "Admin" : "Sales Rep"}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs mono">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`edit-user-${u.id}`}
                    onClick={() => openEdit(u)}
                    className="text-muted-foreground hover:text-primary transition-colors duration-150 mr-3"
                    aria-label="Edit member"
                    title="Edit name / role"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`reset-pw-${u.id}`}
                    onClick={() => openReset(u)}
                    className="text-muted-foreground hover:text-primary transition-colors duration-150 mr-3"
                    aria-label="Reset password"
                    title="Reset password"
                  >
                    <KeyRound className="h-4 w-4" />
                  </button>
                  {me?.id !== u.id && (
                    <button
                      data-testid={`delete-user-${u.id}`}
                      onClick={() => remove(u.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors duration-150"
                      aria-label="Remove member"
                      title="Remove member"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit-member dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit team member</DialogTitle>
            <DialogDescription className="sr-only">
              Update the team member&rsquo;s name or role.
            </DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Editing <strong>{editUser.email}</strong>. To change the password, use the key icon on the Team row.
              </div>
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input
                  data-testid="edit-member-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <select
                  data-testid="edit-member-role"
                  className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full disabled:opacity-60"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  disabled={me?.id === editUser.id}
                >
                  <option value="sales_rep">Sales Rep</option>
                  <option value="admin">Admin</option>
                </select>
                {me?.id === editUser.id && (
                  <div className="text-[11px] text-muted-foreground">You can&rsquo;t change your own role.</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-member-cancel">Cancel</Button>
            <Button
              onClick={submitEdit}
              disabled={editSaving || !editForm.name.trim()}
              data-testid="edit-member-save"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {editSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset-password dialog */}
      <Dialog open={pwOpen} onOpenChange={(o) => { setPwOpen(o); if (!o) { setPwJustSet(null); setPwUser(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pwJustSet ? "New password set" : "Reset password"}</DialogTitle>
            <DialogDescription className="sr-only">
              {pwJustSet ? "Copy and share the newly-generated password securely." : "Set a new password for the team member."}
            </DialogDescription>
          </DialogHeader>
          {pwUser && !pwJustSet && (
            <>
              <div className="space-y-3">
                <div className="text-sm">
                  Set a new password for <strong>{pwUser.name}</strong> (<span className="mono">{pwUser.email}</span>).
                </div>
                <div className="space-y-1.5">
                  <Label>New password</Label>
                  <div className="flex gap-1.5">
                    <Input
                      data-testid="reset-pw-input"
                      value={pwValue}
                      minLength={6}
                      onChange={(e) => setPwValue(e.target.value)}
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => setPwValue(randomTempPassword())} title="Generate">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-[11px] text-muted-foreground">Minimum 6 characters. Share it privately after saving.</div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPwOpen(false)} data-testid="reset-pw-cancel">Cancel</Button>
                <Button
                  onClick={submitReset}
                  disabled={pwSaving || pwValue.length < 6}
                  data-testid="reset-pw-save"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {pwSaving ? "Saving…" : "Set password"}
                </Button>
              </DialogFooter>
            </>
          )}
          {pwJustSet && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Copy and share this password with <strong>{pwJustSet.email}</strong>. Once you close this dialog, we won&rsquo;t show it again.
              </div>
              <CredRow label="Email" value={pwJustSet.email} onCopy={() => copy(pwJustSet.email, "Email copied")} />
              <CredRow label="New password" value={pwJustSet.password} mono onCopy={() => copy(pwJustSet.password, "Password copied")} />
              <DialogFooter>
                <Button onClick={() => setPwOpen(false)} data-testid="reset-pw-close" className="bg-primary text-primary-foreground hover:bg-primary/90">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredRow({ label, value, mono = false, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`flex-1 border border-border rounded-md px-3 py-2 bg-secondary/60 text-sm ${mono ? "mono" : ""}`}>
        {value}
      </div>
      <Button type="button" variant="outline" size="icon" onClick={onCopy} title="Copy">
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}
